import type { Page } from '../../types.js';

/**
 * The programme-plan page.
 *
 * Its one real argument is that the critical path is almost never the software:
 * it is the app stores and the commercial sign-off. So the parallelism table and
 * the store table carry more weight than the effort estimates, and the estimates
 * are deliberately given as ranges by shape rather than as a single number.
 */
const page: Page = {
  slug: 'rollout',
  title: 'Rollout',
  lede:
    'Five stages from decision to live customers. The engineering is the short part; the app stores and your own commercial sign-off are what decide the date.',
  group: 'Delivery',
  blocks: [
    {
      kind: 'stats',
      items: [
        { value: '5', label: 'Stages', sub: 'Choose, register, build, review, launch', accent: 'teal' },
        { value: '1 day', label: 'The backend route', sub: 'One developer, every integration shape', accent: 'blue' },
        { value: '1–2 wk', label: 'Web pilot on widgets', sub: 'From credentials to a real trade', accent: 'blue' },
        { value: 'Weeks', label: 'App store approvals', sub: 'Outside your control and ours', accent: 'rose' },
      ],
    },

    { kind: 'heading', text: 'The five stages' },
    {
      kind: 'ladder',
      steps: [
        {
          title: 'Choose a shape',
          text: 'Which screens you want to own and which you will take from us, and the commercial decisions that come with it: currency, metals, whether customers may sell, your commission, your fees and your limits.',
        },
        {
          title: 'Register',
          text: 'We create your bank on the platform, issue sandbox credentials, and record the web addresses and app identifiers allowed to use them. Getting that list wrong is the most common reason an integration will not start.',
        },
        {
          title: 'Build and pilot in a sandbox',
          text: 'Your backend route, your screens if you are building them, and a real end-to-end journey with test money: look, buy, hold, check, sell, log out, confirm access has ended.',
        },
        {
          title: 'Review',
          text: 'Your security review, your compliance and legal review, and your design review, usually at the same time. Only one of the four reviews is ours, and it is the check that your production configuration matches what you signed off.',
        },
        {
          title: 'Launch',
          text: 'Production credentials, a small internal group with real money at small amounts, then a limited group of customers, then everyone. Production addresses and app identifiers are registered separately from the sandbox ones on purpose.',
        },
      ],
    },

    { kind: 'heading', text: 'What we need from you to register you' },
    {
      kind: 'table',
      columns: ['What we need', 'Why', 'Who provides it'],
      rows: [
        ['The web addresses your app is served from', 'Our screens refuse to render anywhere else. Include staging.', 'Your engineers'],
        ['Your app identifiers, if a mobile app is in scope', 'So we can tell your build from an imitation of it', 'Your mobile team'],
        ['Your brand: name, logos, colours, and the word you use for the product', 'Applied to every surface at once', 'Product and design'],
        ['Your commercial settings: currencies, metals, commission, fees, limits', 'Enforced on every order on our side', 'A product owner, with a sign-off'],
        ['Whether customers may sell', 'Some banks launch as custody only, and the sell path is then refused rather than hidden', 'A product owner'],
        ['Your public key for machine-to-machine calls', 'We store only the public half; your private key never travels', 'Your platform team'],
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'Creating your vault is a deliberate second step',
      text: 'Approving a new bank writes the catalogue entry; creating the database itself is run separately, because the credential that can create databases is deliberately absent from anything reachable over the network. It means onboarding has a short manual step, and that is the trade being made.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'Effort, by shape' },
    {
      kind: 'table',
      columns: ['Shape', 'To a first working trade', 'To a production web launch', 'Team'],
      rows: [
        ['Widget', 'An afternoon', 'One to two weeks', 'One backend developer, one front-end developer part-time'],
        ['Components', 'A day or two', 'Two to four weeks', 'The same, plus a designer for layout'],
        ['SDK', 'A few days', 'Three to six weeks, driven by how many screens you design', 'One backend, one or two front-end, a designer'],
        ['API', 'A sprint', 'Two to three months', 'A backend team, plus everything the SDK route needs'],
      ],
    },
    {
      kind: 'prose',
      text: 'The spread between those four is almost entirely screen work. The backend part is identical in every case and is about a day.',
    },
    {
      kind: 'list',
      items: [
        'Somewhere to keep one credential of ours — your existing secret store. It never goes near an app or a web page.',
        'One route that asks us to start a session for a customer your systems have already logged in.',
        'A reference for each customer: an identifier of your own that means nothing outside your systems.',
        'A logout that ends our session too. In practice, a few lines added to the logout you already have.',
      ],
    },
    {
      kind: 'callout',
      tone: 'stop',
      title: 'The one mistake worth naming in a business document',
      text: 'If that route takes the customer identity from the app rather than from your own login, any logged-in customer can ask to see any other customer\'s holdings. It is one line of code, and it is the only integration mistake that turns into a disclosure incident. Make it an explicit item in your code review and in your penetration test scope.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'App stores are the long pole' },
    {
      kind: 'prose',
      text: 'A web launch goes when your own release process allows; there is no third party in the path. A mobile launch cannot. Each store stands between your finished code and your customers, and none of them is in a hurry.',
    },
    {
      kind: 'table',
      columns: ['Store', 'What you need in place', 'What slows it down'],
      rows: [
        [
          'Apple',
          'A paid organisation account, a verified legal entity, the device-check capability enabled, and your app identifier registered with us',
          'Entity verification can take weeks on its own, and the device checks cannot be exercised in a simulator — your test plan needs real devices and a real signed build',
        ],
        [
          'Google Play',
          'A paid developer account, business verification, the device-integrity service enabled, and two keys recorded from the console',
          'Those keys are shown once, at setup. Nobody writing them down costs a day of confusion later. Financial apps also attract extra declarations at review.',
        ],
        [
          'Huawei AppGallery',
          'An account, an app registration, and the signing fingerprint AppGallery itself issues',
          'AppGallery re-signs your app, so its fingerprint differs from the Google one. Registering the wrong one fails on every Huawei device and looks like a device fault rather than a paperwork error.',
        ],
      ],
    },
    {
      kind: 'callout',
      tone: 'warn',
      title: 'Open the store accounts before anybody writes code',
      text: 'Account approval, business verification and app review are external, sequential, and outside both your control and ours. On a typical mobile programme they take longer than the integration they gate. This is a week-one task for operations, not a week-six task for an engineer.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What can run at the same time' },
    {
      kind: 'split',
      left: {
        title: 'Start on day one, in parallel',
        body: [
          {
            kind: 'list',
            items: [
              'Store accounts and business verification. Owned by operations, blocks nothing else.',
              'Your legal and compliance review of the arrangement.',
              'The backend route. One developer, one day, needs only sandbox credentials.',
              'Screen design, if you are building screens. The biggest single variable in the estimate.',
              'The sandbox pilot, which needs the backend route and nothing else.',
            ],
          },
        ],
      },
      right: {
        title: 'Genuinely sequential',
        body: [
          {
            kind: 'list',
            items: [
              'Store accounts must exist before a signed mobile build can be tested.',
              'A signed build must exist before app review can begin.',
              'Your commercial settings must be signed off before we configure production.',
              'Production credentials come after the reviews, not before.',
            ],
          },
        ],
      },
    },
    {
      kind: 'callout',
      tone: 'tip',
      title: 'Test a closed market on purpose',
      text: 'Almost every pilot runs on a weekday afternoon and never sees the market shut. Put it in the plan explicitly: open the app out of hours, confirm the rate and portfolio still appear, try to buy, and check that what the customer is told is a sentence you are happy to ship. See [when the market is closed](/business/how-it-works#market-closed).',
    },

    { kind: 'heading', text: 'Costs that get left out of a plan' },
    {
      kind: 'list',
      items: [
        'Deciding your commission, your fees and your limits. A commercial decision that needs a named owner, and it holds up launches more often than code does.',
        'Support training. Your agents will be asked why a rate moved between the quote and the confirmation, and what happens to metal a customer holds.',
        'Your own compliance sign-off on selling a commodity product to retail customers. Yours, and not something we can shorten.',
        'Real test devices for a mobile launch, because the device checks do not run in a simulator.',
        'A request-and-confirm loop for configuration changes, for as long as your own staff have no console. See [Operations](/business/operations).',
        'Existing gold holdings, if you have them. Bringing history onto our records is a separate project with its own reconciliation plan, and it is not part of the integration.',
      ],
    },

    { kind: 'divider' },
    {
      kind: 'cards',
      columns: 3,
      items: [
        {
          title: 'Operations',
          text: 'What day two looks like: who can change what, and what your systems receive.',
          href: '/business/operations',
          accent: 'teal',
        },
        {
          title: 'Security and trust',
          text: 'The page to hand your reviewers at the start of stage four rather than the end.',
          href: '/business/security-and-trust',
          accent: 'violet',
        },
        {
          title: 'For your engineers',
          text: 'The developer track, behind a sign-in. Start them there once stage two is done.',
          href: '/developer',
          accent: 'blue',
          cta: 'Open',
        },
      ],
    },
  ],
};

export default page;
